import { useState, useEffect, useCallback } from 'react';
import { configuracionService } from 'services/configuracionService';
import { ConfigClave } from 'types/configuracion.types';

// Caché a nivel de módulo: se comparte entre todos los componentes que usen el hook.
// Una sola llamada al backend por sesión; se invalida cuando actualizar() modifica una clave.
let _cache: Map<string, string> | null = null;
let _inflight: Promise<Map<string, string>> | null = null;

async function fetchConfigs(): Promise<Map<string, string>> {
  if (_cache) return _cache;
  if (_inflight) return _inflight;
  _inflight = configuracionService.listarTodas()
    .then(lista => {
      _cache = new Map(lista.map(c => [c.clave, c.valor]));
      _inflight = null;
      return _cache;
    })
    .catch(err => {
      _inflight = null;
      throw err;
    });
  return _inflight;
}

/**
 * Pre-calienta el caché de configuración. Llamar tras login (AuthContext)
 * para que los flags estén listos antes de que monte cualquier pantalla
 * dependiente (menú, BoletasMasivaPage, etc.). Silencia errores: la lectura
 * fallback ocurrirá al usar el hook en el componente.
 */
export const prefetchConfigInstitucional = (): Promise<void> =>
  fetchConfigs().then(() => undefined).catch(() => undefined);

export const useConfigInstitucional = () => {
  const [configs, setConfigs]   = useState<Map<string, string>>(_cache ?? new Map());
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState<string | null>(null);
  const [saving, setSaving]     = useState<string | null>(null); // clave en guardado
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedKey, setSavedKey] = useState<string | null>(null);

  const cargar = useCallback(async () => {
    if (_cache) { setConfigs(_cache); return; }
    setLoading(true); setError(null);
    try {
      const mapa = await fetchConfigs();
      setConfigs(new Map(mapa));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error al cargar configuración');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const actualizar = async (clave: ConfigClave, valor: string) => {
    setSaving(clave); setSaveError(null); setSavedKey(null);
    try {
      const updated = await configuracionService.actualizar(clave, valor);
      setConfigs(prev => {
        const next = new Map(prev).set(updated.clave, updated.valor);
        _cache = next; // actualizar caché del módulo
        return next;
      });
      setSavedKey(clave); setTimeout(() => setSavedKey(null), 2000);
    } catch (e: unknown) {
      setSaveError(e instanceof Error ? e.message : 'Error al guardar');
    } finally { setSaving(null); }
  };

  const getValor = (clave: ConfigClave): string => configs.get(clave) ?? '';

  return { configs, loading, error, saving, saveError, savedKey, actualizar, getValor, recargar: cargar };
};
